<template>
  <div class="relative h-full">
    <div
      class="z-[2] duration-300 xl:absolute w-full rounded-2xl border border-gray-200 bg-white p-6 min-h-full overflow-hidden"
      :class="{
        'xl:shadow-lg': seeAll,
      }"
    >
      <div
        class="flex flex-col gap-5 mb-4 sm:flex-row sm:justify-between items-center"
      >
        <div class="w-full">
          <h3 class="text-lg font-semibold text-gray-800">Performances</h3>
        </div>

        <div class="relative">
          <div
            class="inline-flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5"
          >
            <button
              v-for="option in options"
              :key="option.value"
              @click="selectPeriod(option)"
              :class="[
                selectedPeriod === option.value
                  ? 'shadow-theme-xs text-gray-900  bg-white'
                  : 'text-gray-500',
                'px-3 py-2 font-medium rounded-md text-theme-sm hover:text-gray-900 hover:shadow-theme-xs',
              ]"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>
      <div
        class="duration-300 overflow-hidden flex"
        :class="{ 'max-h-0': !seeAll, 'max-h-[5rem]': seeAll }"
      >
        <div class="pb-1 max-w-full overflow-auto mx-auto">
          <div
            class="inline-flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5"
          >
            <button
              @click="selectPeriodDate('all')"
              :class="[
                selectedPeriodDate === 'all'
                  ? 'shadow-theme-xs text-gray-900  bg-white'
                  : 'text-gray-500',
                'px-3 py-2 font-medium rounded-md text-theme-sm hover:text-gray-900 hover:shadow-theme-xs',
              ]"
            >
              Tout
            </button>
            <button
              v-for="dateId in options[selectedPeriod]?.periodMarks || []"
              :key="dateId"
              @click="selectPeriodDate(dateId)"
              :class="[
                selectedPeriodDate === dateId
                  ? 'shadow-theme-xs text-gray-900  bg-white'
                  : 'text-gray-500',
                'px-3 py-2 font-medium rounded-md text-theme-sm hover:text-gray-900 hover:shadow-theme-xs',
              ]"
            >
              {{ dateId }}
            </button>
          </div>
        </div>
      </div>
      <div class="max-w-full overflow-auto custom-scrollbar mt-3">
        <div>
          <div
            class="mb-4 overflow-hidden duration-300"
            :class="{
              'max-h-[20rem] ease-out': !seeAll,
              'max-h-[100rem] ease-in': seeAll,
            }"
          >
            <div
              class="flex items-center justify-between pb-4 border-b border-gray-100"
            >
              <span class="text-gray-400 text-theme-xs"> Service </span>
              <span class="text-right text-gray-400 text-theme-xs">
                Total
              </span>
            </div>
            <div
              v-for="(feature, i) in filteredStats"
              :key="i"
              class="flex items-center justify-between py-3 border-b border-gray-100"
            >
              <span
                class="text-gray-500 text-lg"
              >
                {{ feature?.id || 'fonctionalité' }}
              </span>
              <span
                class="text-right text-gray-500 text-lg px-2"
              >
                {{ feature?.count || '0' }}</span
              >
            </div>
          </div>
          <div class="sticky bottom-0 w-full flex mt-auto">
            <button
              @click="toggleSeeAll"
              class="w-full flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white p-2.5 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50"
            >
              {{ seeAll ? 'Réduire' : 'Agrandir' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue'

const props = defineProps({
  statsData: {
    type: Object,
    default: () => ({})
  }
})

const options = {
  year: {
    value: 'year',
    label: 'Année',
    periodMarks: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
  },
  month: {
    value: 'month',
    label: 'Mois',
    periodMarks: Array.from({ length: 31 }, (_, i) => (i + 1).toString()),
  },
  week: {
    value: 'week',
    label: 'Semaine',
    periodMarks: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
  },
}

const selectedPeriod = ref('week')
const selectedPeriodDate = ref('all')
const stats = ref({})
const seeAll = ref(false)

const filteredStats = computed(() => {
  return Object.keys(stats.value)
    .map(key => {
      let value = (stats.value[key] || {})[selectedPeriodDate.value] || 0
      if (selectedPeriodDate.value == 'all') {
        value = sum(Object.values(stats.value[key] || {}))
      }
      return { id: key, count: value }
    })
    .sort((a, b) => b.count - a.count)
})

function sum(numbers) {
  return numbers.reduce((total, n) => total + n, 0)
}

function selectPeriod(option) {
  selectedPeriod.value = option.value
}

function selectPeriodDate(el) {
  selectedPeriodDate.value = el
}

function updateStats() {
  const data = props.statsData || {}
  const res = {}
  const titlesForCurrentPeriod = new Set()

  Object.keys(data).forEach((cityName) => {
    const cityTimeline = data[cityName] || {}
    const periodData = cityTimeline[selectedPeriod.value] || []
    res[cityName] = {}
    periodData.forEach((statItem) => {
      const label = statItem.title || ''
      res[cityName][label] = statItem.count || 0
      titlesForCurrentPeriod.add(label)
    })
  })

  // Mettre à jour les periodMarks de l'option courante avec les vrais titres reçus
  // pour que les filtres de date affichés correspondent aux vraies données
  if (titlesForCurrentPeriod.size > 0) {
    options[selectedPeriod.value].periodMarks = Array.from(titlesForCurrentPeriod)
  }

  stats.value = res
}

watch([() => props.statsData, selectedPeriod], () => {
  updateStats()
}, { deep: true })

onMounted(() => {
  updateStats()
})

function toggleSeeAll() {
  seeAll.value = !seeAll.value
  if (seeAll.value == false) selectPeriodDate('all')
}
</script>

<style scoped>
.area-chart {
  width: 100%;
}
</style>
